import { Dropdown } from '../../UI/Dropdown/Dropdown';
import styles from './EventsFilterWidget.module.scss';
import { DropdownOption } from '../../types/types';
import { Dispatch, SetStateAction, memo } from 'react';
import { SearchInput } from '../../UI/SearchInput/SearchInput';
import useDebounceCallBack from '../../hooks/useDebounce';

interface EventsFilterWidgetProps {
    setPlace: Dispatch<SetStateAction<DropdownOption>>;
    place: DropdownOption;
    setFormat: Dispatch<SetStateAction<DropdownOption>>;
    format: DropdownOption;
    setSearchValue: Dispatch<SetStateAction<string>>;
    // null — «Все»: фильтр по дате не накладывается
    isActual?: boolean | null;
    setIsActual?: Dispatch<SetStateAction<boolean | null>>;
    withAllOption?: boolean;
}

export const EventsFilterWidget = memo(
    ({
        setPlace,
        place,
        setFormat,
        format,
        setSearchValue,
        isActual,
        setIsActual,
        withAllOption = false,
    }: EventsFilterWidgetProps) => {
        const debouncedSearch = useDebounceCallBack(setSearchValue, 1000);

        // Подсчитываем, сколько фильтров будет отображено
        const activeFiltersCount = [
            format ? 1 : 0,
            place ? 1 : 0,
            isActual !== undefined && setIsActual ? 1 : 0,
        ].reduce((acc, val) => acc + val, 0);

        return (
            <div 
                className={styles.widget}
                style={{ '--filters-count': activeFiltersCount } as React.CSSProperties} // передаем в CSS переменную
            >
                <FormatFilter setFormat={setFormat} format={format} />
                <PlaceFilter setPlace={setPlace} place={place} />

                {/* Отображаем фильтр даты ТОЛЬКО если переданы пропсы */}
                {isActual !== undefined && setIsActual && (
                    <DateFilter
                        isActual={isActual}
                        setIsActual={setIsActual}
                        withAllOption={withAllOption}
                    />
                )}

                <SearchInput
                    className={styles.search}
                    placeholder="Поиск"
                    onChange={debouncedSearch}
                />
            </div>
        );
    },
);

const FormatFilter = ({
    setFormat,
    format,
}: {
    setFormat: Dispatch<SetStateAction<DropdownOption>>;
    format: DropdownOption;
}) => {
    const formatOptions = [
        { value: 'OF', label: 'Офлайн' },
        { value: 'ON', label: 'Онлайн' },
        { value: '', label: 'Все' },
    ];

    return (
        <div className={styles.format}>
            <h2 className={styles.format__header}>Формат</h2>
            <Dropdown
                options={formatOptions}
                setCurrentValue={setFormat}
                currentValue={format}
            />
        </div>
    );
};

const PlaceFilter = ({
    setPlace,
    place,
}: {
    setPlace: Dispatch<SetStateAction<DropdownOption>>;
    place: DropdownOption;
}) => {
    const placeOptions = [
        { value: 'Юридический факультет МГУ', label: 'Юридический факультет МГУ' },
        { value: 'Сторонняя организация', label: 'Сторонняя организация' },
        { value: '', label: 'Все' },
    ];

    return (
        <div className={styles.place}>
            <h2 className={styles.format__header}>Место</h2>
            <Dropdown
                options={placeOptions}
                setCurrentValue={setPlace}
                currentValue={place}
            />
        </div>
    );
};

const DateFilter = ({
    isActual,
    setIsActual,
    withAllOption,
}: {
    isActual: boolean | null;
    setIsActual: Dispatch<SetStateAction<boolean | null>>;
    withAllOption: boolean;
}) => {
    const allOption = { value: 'all', label: 'Все' };
    const actualOption = { value: 'actual', label: 'Запланированные' };
    const oldOption = { value: 'old', label: 'Прошедшие' };

    const dateOptions = withAllOption
        ? [allOption, actualOption, oldOption]
        : [actualOption, oldOption];

    const currentOption =
        isActual === null ? allOption : isActual ? actualOption : oldOption;

    return (
        <div className={styles.date}>
            <h2 className={styles.format__header}>Актуальность</h2>
            <Dropdown
                options={dateOptions}
                setCurrentValue={(val: DropdownOption) =>
                    setIsActual(val.value === 'all' ? null : val.value === 'actual')
                }
                currentValue={currentOption}
            />
        </div>
    );
};
